import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, Button, Input, Steps, Avatar, Typography, message, Spin, Tag, Space } from 'antd'
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, FileTextOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { getChapterById, submitChapter, reviewChapter, publishChapter, getReview } from '../../api/textbooks'
import type { Chapter, Review } from '../../api/textbooks'
import './TextbookReview.css'

const { TextArea } = Input
const { Paragraph, Title } = Typography

interface AuthState {
  auth: {
    user: {
      id: string | number
      username: string
      role: string
      roles?: string[]
    } | null
  }
}

const ChapterReview: React.FC = () => {
  const { chapterId } = useParams<{ chapterId: string }>()
  const navigate = useNavigate()
  const user = useSelector((state: AuthState) => state.auth.user)
  
  const [chapter, setChapter] = useState<Chapter | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [reviewComment, setReviewComment] = useState('')
  const [reviewStatus, setReviewStatus] = useState<'approved' | 'rejected' | 'pending'>('pending')
  const [reviews, setReviews] = useState<Review[]>([])

  useEffect(() => {
    if (chapterId) {
      loadChapter()
    }
  }, [chapterId])

  const loadChapter = async () => {
    try {
      setLoading(true)
      const chapterData = await getChapterById(Number(chapterId))
      setChapter(chapterData)
    } catch (error: any) {
      console.error('加载章节失败:', error)
      message.error('加载章节失败: ' + (error.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }

  // 检查用户权限
  const hasPermission = (action: string): boolean => {
    if (!user) return false
    
    const userRoles = user.roles || [user.role]
    
    switch (action) {
      case 'submit':
        return userRoles.includes('AUTHOR') || userRoles.includes('author')
      case 'review':
        return userRoles.includes('DEPUTY_EDITOR') || 
               userRoles.includes('EDITOR_IN_CHIEF') || 
               userRoles.includes('deputy_editor') ||
               userRoles.includes('editor_in_chief')
      case 'publish':
        return userRoles.includes('EDITOR_IN_CHIEF') || 
               userRoles.includes('REVIEW_COMMITTEE') ||
               userRoles.includes('editor_in_chief') ||
               userRoles.includes('review_committee')
      default:
        return false
    }
  }

  // 检查是否是章节创建者
  const isChapterOwner = (): boolean => {
    if (!user || !chapter) return false
    return Number(chapter.created_by) === Number(user.id)
  }

  // 获取审核步骤状态
  const getStepStatus = () => {
    if (!chapter) return []
    
    const steps = [
      { title: '编者提交', status: 'wait' as const, icon: <FileTextOutlined /> },
      { title: '副主编审核', status: 'wait' as const, icon: <ClockCircleOutlined /> },
      { title: '主编审核', status: 'wait' as const, icon: <ClockCircleOutlined /> },
      { title: '发布', status: 'wait' as const, icon: <CheckCircleOutlined /> },
    ]

    switch (chapter.status) {
      case 'draft':
        steps[0].status = 'process'
        break
      case 'submitted':
        steps[0].status = 'finish'
        steps[1].status = 'process'
        break
      case 'reviewing':
        steps[0].status = 'finish'
        steps[1].status = 'finish'
        steps[2].status = 'process'
        break
      case 'approved':
        steps[0].status = 'finish'
        steps[1].status = 'finish'
        steps[2].status = 'finish'
        steps[3].status = 'process'
        break
      case 'published':
        steps.forEach(step => {
          step.status = 'finish'
          step.icon = <CheckCircleOutlined />
        })
        break
      case 'rejected':
        steps[0].status = 'finish'
        steps[1].status = 'error'
        steps[1].icon = <CloseCircleOutlined />
        break
    }

    return steps
  }

  // 提交审核
  const handleSubmit = async () => {
    if (!chapterId) return
    
    if (!isChapterOwner()) {
      message.error('只能提交自己创建的章节')
      return
    }

    try {
      setSubmitting(true)
      await submitChapter(Number(chapterId))
      message.success('章节已提交审核')
      await loadChapter()
    } catch (error: any) {
      console.error('提交审核失败:', error)
      message.error('提交审核失败: ' + (error.message || '未知错误'))
    } finally {
      setSubmitting(false)
    }
  }

  // 审核章节
  const handleReview = async () => {
    if (!chapterId || !reviewComment.trim()) {
      message.warning('请输入审核意见')
      return
    }

    try {
      setSubmitting(true)
      await reviewChapter(Number(chapterId), {
        comment: reviewComment,
        status: reviewStatus
      })
      message.success('审核完成')
      setReviewComment('')
      await loadChapter()
    } catch (error: any) {
      console.error('审核失败:', error)
      message.error('审核失败: ' + (error.message || '未知错误'))
    } finally {
      setSubmitting(false)
    }
  }

  // 发布章节
  const handlePublish = async () => {
    if (!chapterId) return

    try {
      setSubmitting(true)
      await publishChapter(Number(chapterId))
      message.success('章节已发布')
      await loadChapter()
    } catch (error: any) {
      console.error('发布失败:', error)
      message.error('发布失败: ' + (error.message || '未知错误'))
    } finally {
      setSubmitting(false)
    }
  }

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      draft: { color: 'default', text: '草稿' },
      submitted: { color: 'processing', text: '已提交' },
      reviewing: { color: 'processing', text: '审核中' },
      approved: { color: 'success', text: '已通过' },
      rejected: { color: 'error', text: '已退回' },
      published: { color: 'success', text: '已发布' },
    }
    const statusInfo = statusMap[status] || { color: 'default', text: status }
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!chapter) {
    return (
      <Card>
        <Paragraph>章节不存在</Paragraph>
      </Card>
    )
  }

  const steps = getStepStatus()

  return (
    <div className="textbook-review-page">
      <Card className="review-header">
        <Title level={3}>章节审核：{chapter.title}</Title>
        <Space style={{ marginTop: 16 }}>
          {getStatusTag(chapter.status)}
          <span>章节编号：{chapter.chapter_number}</span>
        </Space>
        <Steps items={steps} current={steps.findIndex(s => s.status === 'process')} style={{ marginTop: 24 }} />
      </Card>

      <div className="review-content">
        <Card title="章节内容" className="content-card">
          <Paragraph>章节内容ID: {chapter.content_id}</Paragraph>
          <Paragraph>创建时间: {new Date(chapter.created_at).toLocaleString()}</Paragraph>
          {/* 这里可以添加章节内容的详细展示 */}
        </Card>

        {/* 编者操作区域 */}
        {isChapterOwner() && chapter.status === 'draft' && hasPermission('submit') && (
          <Card title="编者操作" className="review-card">
            <Button 
              type="primary" 
              onClick={handleSubmit}
              loading={submitting}
            >
              提交审核
            </Button>
          </Card>
        )}

        {/* 审核操作区域 */}
        {(chapter.status === 'submitted' || chapter.status === 'reviewing') && hasPermission('review') && (
          <Card title="审核意见" className="review-card">
            <TextArea
              rows={6}
              placeholder="请输入审核意见..."
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
            />
            <Space style={{ marginTop: 16 }}>
              <Button 
                type="primary" 
                onClick={() => {
                  setReviewStatus('approved')
                  handleReview()
                }}
                loading={submitting}
              >
                通过
              </Button>
              <Button 
                danger
                onClick={() => {
                  setReviewStatus('rejected')
                  handleReview()
                }}
                loading={submitting}
              >
                退回修改
              </Button>
            </Space>
          </Card>
        )}

        {/* 发布操作区域 */}
        {chapter.status === 'approved' && hasPermission('publish') && (
          <Card title="发布操作" className="review-card">
            <Button 
              type="primary" 
              size="large"
              onClick={handlePublish}
              loading={submitting}
            >
              发布章节
            </Button>
          </Card>
        )}

        {/* 审核历史 */}
        {reviews.length > 0 && (
          <Card title="审核历史" className="history-card">
            {reviews.map((review) => (
              <div key={review.id} className="review-history-item">
                <div className="review-history-header">
                  <Avatar style={{ backgroundColor: '#1890ff' }}>
                    {review.reviewer_id}
                  </Avatar>
                  <div className="review-history-info">
                    <div className="review-history-author">审核人 ID: {review.reviewer_id}</div>
                    <div className="review-history-time">
                      {new Date(review.created_at).toLocaleString()}
                    </div>
                  </div>
                  <Tag color={review.status === 'approved' ? 'success' : 'error'}>
                    {review.status === 'approved' ? '通过' : '退回'}
                  </Tag>
                </div>
                <Paragraph className="review-history-content">
                  {review.comment}
                </Paragraph>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  )
}

export default ChapterReview

